/* eslint-disable */
import React from "react";
import { first, last } from "lodash";
import { Skeleton } from "@material-ui/lab";
import { CalendarHierarchy, InputCalendarHierarchyItem } from "../../../controls/CalendarHierarchy";
import { useDictionary, useParameter } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { equal } from "../../../../../shared";
import { SLIDE_NUMBER } from "../constants";
import { getDateParts } from "../utils/dateHelpers";

const [yearFrequency, monthFrequency, weekFrequency] = [1, 4, 7];

export const CalendarHierarchyControl: React.FC = () => {
  const [controlValue, setControlValue] = React.useState<number[]>([]);
  const [controlItems, setControlItems] = React.useState<InputCalendarHierarchyItem[] | undefined>();

  const [dictItems, , isLoadingCalendarOrigin] = useDictionary(dictionaryService, "calendar-origin");

  const [commodities] = useParameter<number[]>("@commodities");
  const [calendarValue, setCalendarValue] = useParameter<number[]>("@calendar");
  const [calendarDisplayValue, setCalendarDisplayValue] = useParameter<number[]>("@calendar_display");

  const [dictItemsLastCalendar, , isLoadingDictItemsLastCalendar] = useDictionary(
    dictionaryService,
    "slides-f-last-calendar",
    undefined,
    {
      commodities: commodities ? commodities.join(",") : "120,12,13,18",
      slide: SLIDE_NUMBER,
      attributes: "",
      unit: "",
      countries: "",
    },
  );

  const [minAvailableMonth, maxAvailableMonth] = React.useMemo(() => {
    if (dictItemsLastCalendar != null && dictItemsLastCalendar.length > 0) {
      return [Number(dictItemsLastCalendar[0].id), Number(dictItemsLastCalendar[1].id)];
    }

    return [];
  }, [dictItemsLastCalendar]);

  React.useEffect(
    function initControlValue() {
      if (calendarValue?.length && controlValue.length === 0 && dictItems?.length) {
        const value = calendarValue.reduce((result, calendarId) => {
          const calendarFrequency = Number(String(calendarId)[0]);
          const [isMonth] = [monthFrequency].map((frequency) => frequency === calendarFrequency);
          if (isMonth && !result.includes(calendarId)) {
            result.push(calendarId);
          }
          return result;
        }, [] as number[]);
        setControlValue(value);
      }
    },
    [calendarValue, controlValue, dictItems],
  );

  React.useEffect(
    function updateCalendarValueWhenLastAvailableDatesChange() {
      if (calendarValue != null && minAvailableMonth != null && maxAvailableMonth != null) {
        const sortedCalendarValue = calendarValue.sort();
        const currentlySelectedMinMonth = first(sortedCalendarValue);
        const currentlySelectedMaxMonth = last(sortedCalendarValue);

        const minMonthOverflow = Number(currentlySelectedMinMonth) < Number(minAvailableMonth);
        const maxMonthOverflow = Number(currentlySelectedMaxMonth) > Number(maxAvailableMonth);
        if (minMonthOverflow && maxMonthOverflow) {
          const monthCalendarValues = dictItems?.filter(
            (item) => String(item.getValue<number>("id"))[0] === String(monthFrequency),
          );
          const maxAvailableMonthIdx = monthCalendarValues?.findIndex(
            (item) => item.getValue<number>("id") === maxAvailableMonth,
          );

          if (maxAvailableMonthIdx != null) {
            const newCalendarValues = monthCalendarValues
              ?.slice(maxAvailableMonthIdx - 5, maxAvailableMonthIdx + 1)
              .map((item) => item.getValue<number>("id")!);

            if (newCalendarValues != null) {
              setCalendarDisplayValue(newCalendarValues);
              setCalendarValue(newCalendarValues);
            }
          }
        } else if (minMonthOverflow || maxMonthOverflow) {
          const newCalendarValues = calendarValue.filter(
            (item) => item >= minAvailableMonth && item <= maxAvailableMonth,
          );
          setCalendarDisplayValue(newCalendarValues);
          setCalendarValue(newCalendarValues);
        }
      }
    },
    [minAvailableMonth, maxAvailableMonth],
  );

  React.useEffect(
    function updateStateValueByParameter() {
      if (calendarValue && !equal(calendarValue, controlValue)) {
        setControlValue(calendarValue);
      }
    },
    [calendarValue],
  );

  React.useEffect(
    function updateControlsItemsByDictionaryItems() {
      if (minAvailableMonth != null && maxAvailableMonth != null) {
        const candidatesControlItems = dictItems?.map<InputCalendarHierarchyItem & { frequency: number }>((item) => ({
          id: Number(item.id),
          parent: item.getValue<number>("parent"),
          name: item.getValue<string>("name") || "",
          frequency: item.getValue<number>("frequencyId")!,
        }));

        const { year: fYear } = getDateParts(minAvailableMonth);
        const { year: lYear } = getDateParts(maxAvailableMonth);

        const [minYear, maxYear] = [Number(`1${fYear}0101`), Number(`1${lYear}0101`)];
        const maxMonth = Number(`4${new Date().getFullYear()}1201`);
        const nextControlItems = candidatesControlItems?.reduce((result, item) => {
          if (item.frequency !== weekFrequency) {
            const itemId = item.id as number;
            if (item.frequency === yearFrequency && itemId >= minYear && itemId <= maxYear) {
              result?.push(item);
            } else if (item.frequency === monthFrequency) {
              const paretQuartal = dictItems?.find((dictItem) => equal(+dictItem.id, item.parent));
              const parentYearId = paretQuartal?.getValue<number>("parent")!;
              const maxMonthId = maxAvailableMonth ?? maxMonth;

              if (parentYearId >= minYear && parentYearId <= maxYear && Number(item.id) <= maxMonthId) {
                item.parent = parentYearId;
                result?.push(item);
              }
            }
          }
          return result;
        }, [] as typeof candidatesControlItems);

        setControlItems(nextControlItems);
      }
    },
    [JSON.stringify(dictItems), dictItemsLastCalendar, minAvailableMonth, maxAvailableMonth],
  );

  const handlerChange = React.useCallback(
    (value: unknown) => {
      const monthesOrYearsIds = value as number[];
      if (!equal(calendarDisplayValue, monthesOrYearsIds) || !equal(calendarValue, monthesOrYearsIds)) {
        setCalendarDisplayValue(monthesOrYearsIds);
        setCalendarValue(monthesOrYearsIds);
      }
    },
    [dictItems],
  );

  const loading = isLoadingDictItemsLastCalendar || isLoadingCalendarOrigin;

  return loading ? (
    <Skeleton variant="rect" width="168px" height="51px" />
  ) : (
    <CalendarHierarchy label="Calendar" multiple value={controlValue} onChange={handlerChange} items={controlItems} />
  );
};
